/* eslint-disable max-len */
import React, { useRef, useState } from 'react';
import { AlertTriangle, Upload, CheckCircle, XCircle, Loader2 } from 'lucide-react';

import { AbortError } from '../../errors/errors';
import {  ClientMultipartUploader } from '../../services/ClientMultipartUploader';
import DataAdminServiceFactory from '../../services/DataAdminServiceFactory';
import { estimateSecondsRemaining, getFileNameSansFolder, getTargetFilePathForLocalFile } from '../../utils/fileUtils';
import { formatDuration, formatFileSize } from '../../utils/formatUtils';
import { FileUploadState, FileUploadStatus, initFileUploadState } from '../../models/FileUploadTypes';
import { logEvent, logTrace } from '../../utils/trackingUtils';
import FileInfoIcon from '../sub/FileInfoIcon';

export interface FileSelectModalProps {
  getAccessToken: () => Promise<string>;
  caseFolder: string;
  env: string;
  onClose: () => void;
  siteCode: string;
  subFolder: string;
}

const MAX_RETRIES = 8;

export const FileSelectModal: React.FC<FileSelectModalProps> = (
  { getAccessToken, caseFolder, env, onClose, siteCode, subFolder }
) => {

  const MAX_PARALLEL_UPLOADS = 32;
  const dataAdminService = new DataAdminServiceFactory().create(env);
  const dirInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const [displayCancelConfirm, setDisplayCancelConfirm] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isUploadSessionCompleted, setIsUploadSessionCompleted] = useState<boolean>(false);
  const [isUploadFailed, setIsUploadFailed] = useState<boolean>(false);
  const [isUploadCancelled, setIsUploadCancelled] = useState<boolean>(false);
  const [maxChunksize, setMaxChunksize] = useState<number|null>(null);
  const [maxConcurrency, setMaxConcurrency] = useState<number>(8);
  const [maxRetryAttempts, setMaxRetryAttempts] = useState<number>(MAX_RETRIES);
  const [selectedFiles, setSelectedFiles] = useState<FileUploadState[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [selectFilesEnabled, setSelectFilesEnabled] = useState<boolean>(true);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [uploadEnabled, setUploadEnabled] = useState<boolean>(false);
  const [uploader, setUploader] = useState<ClientMultipartUploader|null>(null);
  const [uploadState, setUploadState] = useState<FileUploadState|null>(null); // State of the current upload.

  const handleClose = (): void => {
    if (isUploading && displayCancelConfirm) {
      setIsUploadCancelled(true);
      uploader?.cancel();
      const filesCompleted = selectedFiles.filter(f => f.status === FileUploadStatus.COMPLETED).length;
      const filesFailed = selectedFiles.filter(f => f.status === FileUploadStatus.FAILED).length;
      const filesAborted = selectedFiles.filter(f => f.status === FileUploadStatus.ABORTED).length;
      const totalQueued = selectedFiles.length;
      const sessionTotalBytes = selectedFiles.reduce((sum, f) => sum + (f.totalFileBytes || 0), 0);
      logEvent({
        action: 'file_upload.session_cancelled',
        siteCode,
        caseFolder,
        subFolder,
        filesQueued: totalQueued,
        filesCompleted,
        filesFailed,
        filesAborted,
        totalBytes: sessionTotalBytes
      });
      logTrace('FileUpload', 'file_upload.session_cancelled', `${siteCode}/${caseFolder}/${subFolder}`);
      setDisplayCancelConfirm(false);
      onClose();
    } else if (isUploading && !displayCancelConfirm) {
      logEvent({
        action: 'file_upload.cancel_prompt_shown',
        siteCode,
        caseFolder,
        subFolder
      });
      logTrace('FileUpload', 'file_upload.cancel_prompt_shown', `${siteCode}/${caseFolder}/${subFolder}`);
      setDisplayCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConcurrencyChange = (activeUploads: number): void => {
    setUploadState(prev => (prev && {
      ...prev,
      currentThreads: activeUploads
    }));
  };

  const handleFileSelect = async (files: FileList | null | undefined, isFolder: boolean = true): Promise<void> => {
    if (!files || files.length === 0) return;

    let selectedFolder = '';
    if (isFolder) {
      selectedFolder = files[0].webkitRelativePath?.split('/')[0] || '';
      setSelectedFolder(selectedFolder + '/');
    } else {
      setSelectedFolder('');
    }

    const existingFiles = await dataAdminService.getFolderObjects(await getAccessToken(), siteCode, caseFolder);

    // Map selected files to FileUploadState, marking already existing files
    let i = 0;
    const filesStates = Array.from(files).map(file => (
      initFileUploadState(
        file,
        i++,
        existingFiles.find(ef => ef.filePathSansCaseFolder === getTargetFilePathForLocalFile (subFolder, file, selectedFolder + '/')) || null
      )
    ));

    setIsUploadSessionCompleted(false);
    setSelectedFiles(filesStates);
    setUploadEnabled(true);

    const totalBytes = filesStates.reduce((sum, f) => sum + (f.file?.size || 0), 0);
    const sampleCount = Math.min(20, filesStates.length);
    const filesSample: { name: string; size: number; path: string }[] = [];
    for (let idx = 0; idx < filesStates.length && filesSample.length < sampleCount; idx++) {
      const s = filesStates[idx];
      if (s.file) {
        filesSample.push({
          name: s.file.name,
          size: s.file.size,
          path: getTargetFilePathForLocalFile(subFolder, s.file, selectedFolder + '/')
        });
      }
    }
    logEvent({
      action: 'file_upload.select',
      siteCode,
      caseFolder,
      subFolder,
      isFolder,
      selectedFolder,
      fileCount: filesStates.length,
      totalBytes,
      filesSampleCount: sampleCount,
      filesSample
    });
    logTrace('FileUpload', 'file_upload.select', `${siteCode}/${caseFolder}/${subFolder}`);
  };

  const handleMaxConcurrencyChange = (newMaxConcurrency: number): void => {
    logEvent({
      action: 'file_upload.settings_change',
      setting: 'maxConcurrency',
      previousValue: maxConcurrency,
      newValue: newMaxConcurrency,
      siteCode,
      caseFolder,
      subFolder
    });
    logTrace('FileUpload', 'file_upload.settings_change', 'maxConcurrency');
    uploader?.setConcurrency(newMaxConcurrency);
    setMaxConcurrency(newMaxConcurrency);
  };

  const handleMaxRetryAttemptsChange = (newMaxRetries: number): void => { 
    logEvent({
      action: 'file_upload.settings_change',
      setting: 'maxRetryAttempts',
      previousValue: maxRetryAttempts,
      newValue: newMaxRetries,
      siteCode,
      caseFolder,
      subFolder
    });
    logTrace('FileUpload', 'file_upload.settings_change', 'maxRetryAttempts');
    uploader?.setMaxRetries(newMaxRetries);
    setMaxRetryAttempts(newMaxRetries);
  };

  const handleMaxChunkSizeChange = (value: string): void => {
    const newSize = value ? Number(value) : null;
    logEvent({
      action: 'file_upload.settings_change',
      setting: 'maxChunksize',
      previousValue: maxChunksize ?? 'default',
      newValue: newSize ?? 'default',
      siteCode,
      caseFolder,
      subFolder
    });
    logTrace('FileUpload', 'file_upload.settings_change', 'maxChunksize');
    setMaxChunksize(newSize);
  };

  const handleUploadFailure = (
    fileIndex: number,
    attemptCount: number,
    error: unknown,
    status: FileUploadStatus
  ): void => {
    if (status === FileUploadStatus.RETRYING) {
      const file = selectedFiles[fileIndex]?.file;
      const objectKey = file ? getTargetFilePathForLocalFile(subFolder, file, selectedFolder) : '';
      const message = error instanceof Error ? error.message : String(error);
      logEvent({
        action: 'file_upload.retry',
        siteCode,
        caseFolder,
        subFolder,
        objectKey,
        attempt: attemptCount,
        error: message
      });
      logTrace('FileUpload', 'file_upload.retry', objectKey || `${siteCode}/${caseFolder}/${subFolder}`);
    }
    // Set file status to indicate retry on failure.
    setSelectedFiles(prev => prev.map((fileState, index) => 
      index === fileIndex ? {
        ...fileState,
        attemptCount: Math.max(attemptCount, fileState.attemptCount), // Show highest attempt count across threads.
        error: error instanceof Error ? error.message : String(error),
        status: status
      } :
        fileState
    ));
    setUploadState(prev => (prev && {
      ...prev,
      attemptCount: Math.max(attemptCount, prev.attemptCount), // Show highest attempt count across threads.
      status: status
    }));
  };

  const handleUploadProgress = (fileIndex: number, newBytesUploaded: number, uploadStatus: FileUploadStatus): void => {
    setSelectedFiles(prev => prev?.map((fileState, index) => 
      index === fileIndex ? { 
        ...fileState,
        status: uploadStatus,
        startTime: fileState.startTime || Date.now(),
      } :
        fileState
    ));
    setUploadState(prev => (prev && {
      ...prev,
      uploadedBytes: prev.uploadedBytes + newBytesUploaded,
      progress: Math.round((prev.uploadedBytes + newBytesUploaded) / prev.totalFileBytes * 100),
      estimatedSecondsRemaining: prev.startTime ?
        estimateSecondsRemaining(prev.startTime, prev.uploadedBytes + newBytesUploaded, prev.totalFileBytes) : null,
      status: uploadStatus
    }));
  };

  const handleFileRemove = (index: number): void => {
    const removed = selectedFiles[index];
    const filteredFiles = selectedFiles
      .filter((fileState, i) => i !== index && fileState.status !== FileUploadStatus.UPLOADING)
      .map((fileState, i) => ({ ...fileState, index: i })); // Re-index remaining files
    setSelectedFiles(filteredFiles);
    if (removed?.file) {
      const objectKey = getTargetFilePathForLocalFile(subFolder, removed.file, selectedFolder);
      logEvent({
        action: 'file_upload.remove',
        siteCode,
        caseFolder,
        subFolder,
        objectKey,
        queueSize: filteredFiles.length
      });
      logTrace('FileUpload', 'file_upload.remove', objectKey);
    }
    if (filteredFiles.length <= 0) {
      // Clear file selections
      if (dirInputRef.current) dirInputRef.current.value = '';
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadEnabled(false);
    }
  };

  /**
   * Orchestrates the sequential upload of selected files.
   */
  const handleUploadClick = async (): Promise<void> => {
    setIsUploading(true);
    setUploadEnabled(false);
    setSelectFilesEnabled(false);
    setIsUploadCancelled(false);
    setIsUploadSessionCompleted(false);
    setIsUploadFailed(false);

    const totalBytes = selectedFiles.reduce((sum, f) => sum + (f.file?.size || 0), 0);
    logEvent({
      action: 'file_upload.session_start',
      siteCode,
      caseFolder,
      subFolder,
      filesQueued: selectedFiles.length,
      totalBytes,
      maxConcurrency,
      maxRetryAttempts,
      maxChunksize: maxChunksize ?? undefined
    });
    logTrace('FileUpload', 'file_upload.session_start', `${siteCode}/${caseFolder}/${subFolder}`);

    let isAborted = false;
    let uploadFatalError: string|null = null;
    
    for (let i = 0; i < selectedFiles.length; i++) {
      setUploadState(initFileUploadState(
        selectedFiles[i].file,
        i,
        selectedFiles[i].existingFile
      ));
      const uploader = new ClientMultipartUploader(
        env,
        getAccessToken,
        siteCode,
        caseFolder,
        (newBytesUploaded: number, uploadStatus: FileUploadStatus) => { // progress handler
          handleUploadProgress(i, newBytesUploaded, uploadStatus);
        },
        handleConcurrencyChange,
        (attempt: number, error: unknown, status: FileUploadStatus) => { // failure handler
          handleUploadFailure(i, attempt, error, status); 
        },
        maxConcurrency,
        maxRetryAttempts
      );
      setUploader(uploader);

      if (isUploadCancelled || isAborted || uploadFatalError !== null) {
        // Update status to ABORTED for all files after aborted file or fatal error.
        setSelectedFiles(prev => prev.map((fileState, index) =>
          index === i ? {
            ...fileState,
            endTime: Date.now(),
            status: FileUploadStatus.ABORTED
          } :
            fileState
        ));

        // Skip this loop iteration
        continue;
      }

      const fileState = selectedFiles[i];
      if (fileState.file) {

        // The selected local folder is removed from the file path when creating the object key.
        const objectKey = getTargetFilePathForLocalFile(subFolder, fileState.file, selectedFolder);

        if (uploadFatalError === null) {
          try {
            await uploader.managedUpload(
              fileState.file,
              objectKey,
              maxChunksize
            );
  
            // Update status to COMPLETED
            setSelectedFiles(prev => prev.map((fileState, index) => 
              index === i ? {
                ...fileState,
                endTime: Date.now(),
                status: FileUploadStatus.COMPLETED
              } :
                fileState
            ));
          } catch (error) {
            if (error instanceof AbortError) {
              console.error('Upload cancelled.');
              setIsUploadCancelled(true);
              isAborted = true;
            } else {
              uploadFatalError = error instanceof Error ? error.message : String(error);
              setIsUploadFailed(true);
            }
            console.error('Upload failed:', error);
            logEvent({
              action: 'file_upload.file_error',
              siteCode,
              caseFolder,
              subFolder,
              objectKey,
              error: uploadFatalError
            });
            logTrace('FileUpload', 'file_upload.file_error', objectKey);
          }
        }

        if (uploadFatalError !== null) {
          uploader.cancel(); // Ensure any other threads are aborted.
          setIsUploadFailed(true);
          setSelectedFiles(prev => prev.map((fileState, index) =>
            index === i ? {
              ...fileState,
              endTime: Date.now(),
              error: uploadFatalError,
              status: FileUploadStatus.FAILED
            } :
              fileState
          ));
        } else if (isAborted) {
          setSelectedFiles(prev => prev.map((fileState, index) =>
            index === i ? {
              ...fileState,
              endTime: Date.now(),
              status: FileUploadStatus.ABORTED
            } :
              fileState
          ));
        }

      }
    }

    // Log end of session with outcomes
    const filesCompleted = selectedFiles.filter(f => f.status === FileUploadStatus.COMPLETED).length;
    const filesFailed = selectedFiles.filter(f => f.status === FileUploadStatus.FAILED).length;
    const filesAborted = selectedFiles.filter(f => f.status === FileUploadStatus.ABORTED).length;
    const totalQueued = selectedFiles.length;
    const sessionTotalBytes = selectedFiles.reduce((sum, f) => sum + (f.totalFileBytes || 0), 0);
    logEvent({
      action: 'file_upload.session_end',
      siteCode,
      caseFolder,
      subFolder,
      filesQueued: totalQueued,
      filesCompleted,
      filesFailed,
      filesAborted,
      totalBytes: sessionTotalBytes,
      isUploadFailed,
      isUploadCancelled
    });
    logTrace('FileUpload', 'file_upload.session_end', `${siteCode}/${caseFolder}/${subFolder}`);

    setIsUploading(false);
    setIsUploadSessionCompleted(true);
  };

  return (
    <div className="modal-background">
      <div ref={modalRef} className="modal-content w-full h-full max-w-8/10 max-h-9/10">

        <div className="modal-header">
          <h2 className="text-2xl font-semibold text-gray-900">File Upload</h2>
          <button
            className="modal-close-button"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <span className="text-2xl text-gray-500 hover:text-gray-700"> × </span>
          </button>
        </div>

        <div className="flex-1 p-4 bg-gray-50 overflow-hidden flex flex-col w-full">
          <h3>
            Upload files to case folder: <span>{caseFolder}</span>
            {subFolder && (
              <span> /{subFolder}</span>
            )}
          </h3>

          <div data-testid="controls-div" className="w-full flex items-center justify-evenly mt-4 mb-2">
            <div className="flex items-center gap-3">
              <button
                className={`${selectFilesEnabled ? 'action-primary' : 'action-disabled'} `}
                disabled={!selectFilesEnabled}
                onClick={() => dirInputRef.current?.click()}
              >
                Select a folder
              </button>
              <input
                ref={dirInputRef}
                type="file"
                style={{ display: 'none' }}
                // @ts-expect-error: webkitdirectory is not in the spec
                webkitdirectory=""
                directory=""
                onChange={() => handleFileSelect(dirInputRef.current?.files)}
              />

              <span className="plr-2 font-bold">Or</span>

              <button
                className={`${selectFilesEnabled ? 'action-primary' : 'action-disabled'} `}
                disabled={!selectFilesEnabled}
                onClick={() => fileInputRef.current?.click()}
              >
                Select files
              </button>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                multiple
                onChange={() => handleFileSelect(fileInputRef.current?.files, false)}
              />
            </div>

            <div>
              <button
                data-testid="upload-button"
                type="button"
                className={`mx-4 ${uploadEnabled ? 'action-primary' : 'action-disabled'} flex items-center gap-2`}
                disabled={!uploadEnabled}
                onClick={() => handleUploadClick()}
              >
                <Upload className="w-5 h-5" />
                <span>Upload Files</span>
              </button>
            </div>
          </div>

          {(!isUploading && !selectedFiles.length) && (  
            <div id="info-text-noselection" className="m-6 text-sm text-gray-600 text-center">
              Select files or a folder to upload. Maximum file size is 5 TB.
            </div>
          )}

          {(!isUploading && selectedFiles.length > 0 && !isUploadSessionCompleted) && (  
            <div id="info-text-selected" className="m-6 text-sm text-gray-600 text-center">
              {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected, ready to upload.
            </div>
          )}

          {(uploadState && isUploading && !isUploadSessionCompleted) && (
            <>
              <div className="text-warning">
                <b>NOTE: To ensure your upload is not interrupted:</b>
                <ul className="list-disc list-inside">
                  <li>Do not close your browser or navigate away</li>
                  <li>Do not let your device enter sleep mode</li>
                </ul>
              </div>
              <div>
                <div id="info-text-uploading" className="m-2 text-sm text-gray-600 flex flex-row justify-evenly items-center width-full">
                  <div>Uploaded {selectedFiles.filter(f => f.status === 'COMPLETED').length} of {selectedFiles.length} files...</div>
                  <div>Concurrent threads: {uploadState.currentThreads}</div>
                </div>
              </div>

            </>
          )}

          {(isUploadSessionCompleted) && (  
            <div id="info-text-selected" className="m-6 text-sm text-gray-600 text-center">
              { (isUploadFailed) && (
                <>Upload session completed with errors. You may close this modal dialog.</>
              )}
              { (!isUploadFailed) && (
                <>Upload session is complete. You may close this modal dialog.</>
              )}
            </div>
          )}

          {selectedFiles && selectedFiles.length > 0 && (
            <div className="flex-1 bg-white rounded-lg shadow overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr className="px-4">
                      <th className="header-text w-8/10">File Name</th>
                      <th className="header-text w-2/10">Size</th>
                      <th className="header-text w-2/10">Upload Status</th>
                      <th id="header-info-icon" className="header-text w-1/10"></th>
                      <th className="w-1/10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(selectedFiles).map(fileState => (
                      fileState.file && (
                        <React.Fragment key={fileState.file?.webkitRelativePath || fileState.file?.name}>
                          <tr
                            className={`border-b border-gray-100 ${
                              fileState.existingFile && fileState.status === 'PENDING'
                                ? 'bg-yellow-50'
                                : ''
                            }`}
                          >
                            <td className="text-left py-3 px-4">{getFileNameSansFolder(fileState.file, selectedFolder)}</td>
                            <td className="text-left py-3 px-4">{formatFileSize(fileState.file.size || 0)}</td>
                            <td className="text-left py-3 px-4">
                              <div className="flex items-center gap-2">
                                {fileState.status === FileUploadStatus.ABORTED && (
                                  <span className="text-orange-600">ABORTED</span>
                                )}
                                {fileState.status === FileUploadStatus.PENDING && (
                                  <span className="text-gray-600">PENDING</span>
                                )}
                                {fileState.status === FileUploadStatus.INITIALIZING && (
                                  <>
                                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                                    <span className="text-blue-600">INITIALIZING</span>
                                  </>
                                )}
                                {fileState.status === FileUploadStatus.UPLOADING && (
                                  <>
                                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                                    <span className="text-blue-600">UPLOADING</span>
                                  </>
                                )}
                                {fileState.status === FileUploadStatus.COMPLETED && (
                                  <>
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <span className="text-green-600">COMPLETED</span>
                                  </>
                                )}
                                {fileState.status === FileUploadStatus.FAILED && (
                                  <>
                                    <XCircle className="w-4 h-4 text-red-600" />
                                    <span className="text-red-600">FAILED</span>
                                  </>
                                )}
                                {fileState.status === FileUploadStatus.RETRYING && (
                                  <>
                                    <Loader2 className="w-4 h-4 text-orange-600 animate-spin" />
                                    
                                    <span className="text-orange-600">RETRYING</span>
                                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                                  </>
                                )}
                              </div>
                            </td>

                            <td className="info-icon-cell text-center">
                              <FileInfoIcon fileState={fileState} boundaryRef={modalRef} />
                            </td>

                            <td>
                              <button
                                type="button"
                                disabled={(fileState.status !== FileUploadStatus.PENDING)}
                                onClick={() => handleFileRemove(fileState.index)}
                              >
                                X
                              </button>
                            </td>
                          </tr>

                          {fileState.status === FileUploadStatus.UPLOADING && uploadState && (
                            <tr className="bg-blue-50">
                              <td colSpan={5} className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                      style={{ width: `${uploadState.progress}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-sm text-gray-600">{uploadState.progress}%</span>
                                </div>
                                <div>
                                  {formatFileSize(uploadState.uploadedBytes)} of {formatFileSize(uploadState.totalFileBytes)} bytes
                                </div>
                                <div>
                                  {uploadState.estimatedSecondsRemaining !== null && (
                                    <span>
                                      Estimated time remaining:
                                      {formatDuration(uploadState.estimatedSecondsRemaining)}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    ))}
                  </tbody>
                </table>

              </div>
            </div>
          )}

          <div className="mt-4 justify-center flex">
            <button
              type="button"
              data-testid="advanced-options-toggle"
              className="action-subtle"
              onClick={() => setShowAdvanced(prev => !prev)}
            >
              {showAdvanced ? 'Hide Advanced Options' : 'Advanced Options'}
            </button>
          </div>

          {showAdvanced && (
            <div
              data-testid="advanced-options-container"
              className="flex items-center justify-center gap-3 mt-2"
            >
              <div>
                <span>Max Upload Workers:</span>
                <select
                  className="border border-gray-300 rounded px-2 py-1"
                  value={maxConcurrency}
                  onChange={e => handleMaxConcurrencyChange(Number(e.target.value))}
                  disabled={!uploadEnabled && !isUploading}
                >
                  {Array.from({ length: MAX_PARALLEL_UPLOADS }, (_, i) => i + 1).map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>

              <div>
                <span>Retry Attempts:</span>
                <select
                  className="border border-gray-300 rounded px-2 py-1"
                  value={maxRetryAttempts}
                  onChange={e => handleMaxRetryAttemptsChange(Number(e.target.value))}
                  disabled={!uploadEnabled && !isUploading}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>

              <div>
                <span>Max Chunk size:</span>
                <select
                  className="border border-gray-300 rounded px-2 py-1"
                  value={maxChunksize || ''}
                  onChange={e => handleMaxChunkSizeChange(e.target.value)}
                  disabled={!uploadEnabled}
                >
                  <option value="">Default</option>
                  <option value={50 * 1024 * 1024}>50 MB</option>
                  <option value={100 * 1024 * 1024}>100 MB</option>
                  <option value={150 * 1024 * 1024}>150 MB</option>
                  <option value={200 * 1024 * 1024}>200 MB</option>
                  <option value={500 * 1024 * 1024}>500 MB</option>
                </select>
              </div>
            </div>
          )}

          { /* Displayed if user tries to close while uploading */ }
          {displayCancelConfirm && (
            <div id="modal-background-cancel-confirm" className="fixed inset-0 z-60 flex items-center justify-center bg-black/30 backdrop-blur-md">
              <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md mx-4">
                <div className="text-center">
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-6 py-4 text-yellow-800">
                    <p className="mb-4">Are you sure you want to close? Any uploads in progress will be cancelled.</p>

                    <div className="flex flex-col gap-3">
                      <button
                        className="action-secondary"
                        onClick={handleClose}
                      >
                        Yes, Cancel Upload and Close
                      </button>
                      <button
                        className="action-primary"
                        onClick={() => setDisplayCancelConfirm(false)}
                      >
                        No, Continue Upload
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
